import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import type { FormSchema } from '@kfolks/types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DynamicFormRenderer } from '@/modules/forms/DynamicFormRenderer'
import { requestCenterApi } from '@/services/request-center-api'

/**
 * Submit a new request. Loads the type by slug, hands its formSchema to
 * DynamicFormRenderer, and POSTs the validated formData on submit. On
 * success, redirects to the request's detail page.
 */
export function NewRequestPage() {
	const { slug } = useParams<{ slug: string }>()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const [formData, setFormData] = useState<Record<string, unknown>>({})
	// Sibling stream of human-readable labels emitted by autocomplete /
	// data-source widgets. Shipped on submit as `formDataLabels` so the
	// backend persists them on `formData.__labels` for the row summary on
	// /my, dashboard widgets, and the digest. Static-options labels are
	// resolved server-side from the schema; this map only carries the
	// dynamic ones the frontend already had cached at pick time.
	const [formDataLabels, setFormDataLabels] = useState<Record<string, string>>({})

	const typeQuery = useQuery({
		queryKey: ['request-type', slug],
		queryFn: () => requestCenterApi.getRequestTypeBySlug(slug ?? ''),
		enabled: Boolean(slug),
	})

	const submit = useMutation({
		mutationFn: (input: {
			requestTypeId: string
			formData: Record<string, unknown>
			formDataLabels?: Record<string, string>
		}) => requestCenterApi.submitRequest(input),
		onSuccess: (created) => {
			void queryClient.invalidateQueries({ queryKey: ['requests'] })
			void navigate(`/requests-center/${created.id}`, { replace: true })
		},
	})

	if (!slug) return <ErrorState message="Missing request type slug." />
	if (typeQuery.isLoading) return <FormSkeleton />
	if (typeQuery.error) return <ErrorState message={messageForLoadError(typeQuery.error)} />
	const type = typeQuery.data
	if (!type) return <ErrorState message="Request type not available." />

	return (
		<NewRequestForm
			type={type}
			formData={formData}
			onChange={setFormData}
			onLabelsChange={setFormDataLabels}
			onSubmit={(data) =>
				submit.mutate({
					requestTypeId: type.id,
					formData: data,
					formDataLabels: Object.keys(formDataLabels).length > 0 ? formDataLabels : undefined,
				})
			}
			submitting={submit.isPending}
			submitError={submit.error}
		/>
	)
}

function NewRequestForm({
	type,
	formData,
	onChange,
	onLabelsChange,
	onSubmit,
	submitting,
	submitError,
}: {
	type: { id: string; name: string; description: string | null; formSchema: FormSchema }
	formData: Record<string, unknown>
	onChange: (next: Record<string, unknown>) => void
	onLabelsChange: (next: Record<string, string>) => void
	onSubmit: (next: Record<string, unknown>) => void
	submitting: boolean
	submitError: unknown
}) {
	return (
		<div className="mx-auto max-w-2xl space-y-6">
			<header className="space-y-2">
				<h1 className="text-2xl font-semibold tracking-tight">{type.name}</h1>
				{type.description ? <p className="text-sm text-muted-foreground">{type.description}</p> : null}
			</header>
			<Card>
				<CardHeader>
					<CardTitle className="text-base">Fill in the details</CardTitle>
				</CardHeader>
				<CardContent>
					<DynamicFormRenderer
						formSchema={type.formSchema}
						value={formData}
						onChange={onChange}
						onLabelsChange={onLabelsChange}
						onSubmit={onSubmit}
						submitting={submitting}
						submitLabel="Submit request"
					/>
					{submitError ? <SubmitError error={submitError} /> : null}
				</CardContent>
			</Card>
		</div>
	)
}

function messageForLoadError(error: unknown): string {
	const status = isAxiosError(error) ? error.response?.status : undefined
	return status === 404
		? 'Request type not found, or you do not have access to it.'
		: 'Failed to load the request type.'
}

function SubmitError({ error }: { error: unknown }) {
	const apiMessage =
		isAxiosError(error) && (error.response?.data as { error?: { message?: string } } | undefined)?.error?.message
	const fallback = error instanceof Error ? error.message : 'Submission failed'
	return (
		<p role="alert" className="mt-3 text-sm text-destructive">
			{apiMessage ?? fallback}
		</p>
	)
}

function FormSkeleton() {
	return (
		<div className="mx-auto max-w-2xl space-y-3">
			<div className="h-7 w-1/3 animate-pulse rounded bg-muted" />
			<div className="h-40 animate-pulse rounded-lg border bg-muted/40" />
		</div>
	)
}

function ErrorState({ message }: { message: string }) {
	return (
		<div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
			{message}
		</div>
	)
}
